import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import cashboxService from '../services/cashboxService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { X } from 'lucide-react';

const CashboxNewTransaction = () => {
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState({
        transaction_type: 'INCOME',
        category_code: '',
        description: '',
        amount: '',
        reference_date: format(new Date(), 'yyyy-MM-dd'),
        payment_method: 'Transferência',
        bank_account: '',
        document_number: '',
        notes: ''
    });

    const createMutation = useMutation({
        mutationFn: cashboxService.createTransaction,
        onSuccess: (response) => {
            toast.success('Transação registrada com sucesso!');
            queryClient.invalidateQueries(['cashbox-stats']);
            queryClient.invalidateQueries(['cashbox-transactions']);
            // Reset form
            setFormData({
                transaction_type: 'INCOME',
                category_code: '',
                description: '',
                amount: '',
                reference_date: format(new Date(), 'yyyy-MM-dd'),
                payment_method: 'Transferência',
                bank_account: '',
                document_number: '',
                notes: ''
            });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Erro ao registrar transação');
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        const amount = parseFloat(formData.amount);
        if (!amount || amount <= 0) {
            toast.error('Valor deve ser maior que zero');
            return;
        }
        if (!formData.category_code) {
            toast.error('Selecione uma categoria');
            return;
        }
        if (!formData.description || !formData.description.trim()) {
            toast.error('Descrição é obrigatória');
            return;
        }

        createMutation.mutate(formData);
    };

    const categories = {
        INCOME: [
            { code: 'VENDAS', name: 'Vendas' },
            { code: 'RECEBIMENTO_CLIENTES', name: 'Recebimento de Clientes' },
            { code: 'RENDIMENTOS_FINANCEIROS', name: 'Rendimentos Financeiros' },
            { code: 'OUTRAS_RECEITAS', name: 'Outras Receitas' }
        ],
        EXPENSE: [
            { code: 'FORNECEDORES', name: 'Pagamento a Fornecedores' },
            { code: 'DESPESAS_FIXAS', name: 'Despesas Fixas' },
            { code: 'IMPOSTOS_TAXAS', name: 'Impostos e Taxas' },
            { code: 'MANUTENCAO_EQUIPAMENTOS', name: 'Manutenção de Equipamentos' },
            { code: 'DESPESAS_ADMINISTRATIVAS', name: 'Despesas Administrativas' },
            { code: 'OUTRAS_DESPESAS', name: 'Outras Despesas' }
        ],
        INVESTMENT: [
            { code: 'INVESTIMENTO_MAQUINAS', name: 'Investimento em Máquinas' },
            { code: 'INVESTIMENTO_VEICULOS', name: 'Investimento em Veículos' },
            { code: 'INVESTIMENTO_IMOVEIS', name: 'Investimento em Imóveis' },
            { code: 'INVESTIMENTO_TECNOLOGIA', name: 'Investimento em Tecnologia' },
            { code: 'INVESTIMENTO_EXPANSÃO', name: 'Investimento de Expansão' },
            { code: 'OUTROS_INVESTIMENTOS', name: 'Outros Investimentos' }
        ]
    };

    const currentCategories = categories[formData.transaction_type] || [];

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                        <h2 className="text-xl font-bold text-gray-900">Nova Movimentação de Caixa</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Tipo de Operação
                                </label>
                                <select
                                    value={formData.transaction_type}
                                    onChange={(e) => {
                                        setFormData({
                                            ...formData,
                                            transaction_type: e.target.value,
                                            category_code: ''
                                        });
                                    }}
                                    className="input"
                                >
                                    <option value="INCOME">Entrada de Valor</option>
                                    <option value="EXPENSE">Saída de Valor</option>
                                    <option value="INVESTMENT">Investimento</option>
                                    <option value="INVESTMENT_RETURN">Retorno de Investimento</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Categoria
                                </label>
                                <select
                                    value={formData.category_code}
                                    onChange={(e) => setFormData({ ...formData, category_code: e.target.value })}
                                    className="input"
                                    required
                                >
                                    <option value="">Selecione...</option>
                                    {currentCategories.map((cat) => (
                                        <option key={cat.code} value={cat.code}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Descrição
                                </label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="input"
                                    placeholder="Ex: Aporte inicial do sócio, Compra de Servidor"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Valor (R$)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    className="input font-bold"
                                    placeholder="0,00"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Data
                                </label>
                                <input
                                    type="date"
                                    value={formData.reference_date}
                                    onChange={(e) => setFormData({ ...formData, reference_date: e.target.value })}
                                    className="input"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Conta/Banco
                                </label>
                                <input
                                    type="text"
                                    value={formData.bank_account}
                                    onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                                    className="input"
                                    placeholder="Ex: Santander, Caixa, Cofre"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Nº Documento/Ref
                                </label>
                                <input
                                    type="text"
                                    value={formData.document_number}
                                    onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                                    className="input"
                                    placeholder="Opcional"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Observações Adicionais
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                className="input min-h-[80px]"
                                rows="2"
                            />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setFormData({
                                        transaction_type: 'INCOME',
                                        category_code: '',
                                        description: '',
                                        amount: '',
                                        reference_date: format(new Date(), 'yyyy-MM-dd'),
                                        payment_method: 'Transferência',
                                        bank_account: '',
                                        document_number: '',
                                        notes: ''
                                    });
                                }}
                                className="btn-secondary flex-1"
                            >
                                Limpar
                            </button>
                            <button
                                type="submit"
                                disabled={createMutation.isPending}
                                className="btn-primary flex-1 shadow-lg shadow-primary-100"
                            >
                                {createMutation.isPending ? 'Registrando...' : 'Confirmar Lançamento'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CashboxNewTransaction;
